import React, { useState } from 'react';
import _ from 'lodash';
import { useSirenEntity, useAccountFeatures, store, storeActions } from '~/utility/BinSentry-ui-utility';
import { Button, makeStyles } from '~/lib/bn-material-ui';
import OrderCalculatorDialog from '../order-calculator/order-calculator-dialog';
import { OrderPlannerDialog } from '../orders/dialogs/order-planner-dialog/order-planner-dialog';
import { DialogStylesProvider, InLineStylesProvider } from '~/parcels/parcel-style-providers';

const useStyles = makeStyles(({ components }) => {
  const overrides = components.MuiButton.styleOverrides;
  return {
    /**
     * Inject contained button styling here since parcels don't have access to
     * the mui theme (even though it's provided with ThemeProvider). It's likely
     * caused by an race condition on the parcel & react env initializations.
     */
    button: {
      ...overrides.root,
      ...overrides.sizeMedium,
      ...overrides.outlinedPrimary,
      cursor: 'pointer',
    },
  };
});

export function OpenOrderButton(inputProps) {
  const styles = useStyles();
  const { parcel, orderHref: orderHrefProp, orderEntity: orderEntityProp } = inputProps;

  const [dialogOpen, setDialogOpen] = useState(false);
  const { entity: orderEntityFromHref, loading: loadingOrderEntity } = useSirenEntity({ href: orderHrefProp });
  const orderEntity = orderEntityProp || orderEntityFromHref;
  const orderHref = orderEntity?.getLink('self').href;
  const firstAllocation = orderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/order-bin-allocation');
  const binId = firstAllocation?.getLinkByRel('https://api.binsentry.com/rel/bin')?.href;
  const { features, loadingFeatures } = useBinsOwnerAndFeatures({ binId });

  const loading = loadingOrderEntity || loadingFeatures;
  const props = { disabled: loading };
  if (!loading) {
    props.disabled = false;
    props.onClick = () => setDialogOpen(true);
  }

  const mergedProps = { ...props, ..._.omit(inputProps, ['orderHref', 'orderEntity', 'insertionPoint', 'parcel']) };
  const orderDialogToRender = features.orderPlanner ?
    <OrderPlannerDialog
      onClose={() => setDialogOpen(false)}
      open={dialogOpen}
      initialSelectedOrderId={orderHref}
    /> :
    <OrderCalculatorDialog
      open={dialogOpen}
      binHref={binId}
      activeOrderId={orderHref}
      onClose={() => setDialogOpen(false)}
    />;

  const button = <Button className={styles.button} {...mergedProps}>
    {!mergedProps.children ?
      <span title='Order Number'>Order {orderEntity?.properties.orderNumber}</span> :
      mergedProps.children
    }
  </Button>;

  if (!parcel) {
    return <>
      {dialogOpen && orderDialogToRender}
      {button}
    </>;
  }

  return <>
    <DialogStylesProvider>
      {dialogOpen && orderDialogToRender}
    </DialogStylesProvider>
    <InLineStylesProvider insertionPoint={inputProps.insertionPoint}>
      {button}
    </InLineStylesProvider>
  </>;
}

function useBinsOwnerAndFeatures({ binId }) {
  const { entity: binEntity } = useSirenEntity({ href: binId });
  const ownerId = binEntity?.getSubEntityByRel('https://api.binsentry.com/rel/owner')?.href;
  const { entity: owner } = useSirenEntity({ href: ownerId });
  const { features, loading: loadingFeatures } = useAccountFeatures({ organizationId: ownerId });

  return {
    features,
    loadingFeatures,
    owner,
    ownerId,
  };
}

export function OpenBinOrdersButton(inputProps) {
  const styles = useStyles();
  const { binHref } = inputProps;
  const [dialogOpen, setDialogOpen] = useState(false);
  const { owner, ownerId, features, loadingFeatures } = useBinsOwnerAndFeatures({ binId: binHref });
  const canViewOrders = owner?.hasLink('https://api.binsentry.com/rel/search-orders');

  if (!canViewOrders || loadingFeatures) {
    return null;
  }

  const props = {
    onClick: () => {
      features.orderPlanner
        ? store.dispatch(storeActions.navigate('/order-scheduler', { organizationId: ownerId }))
        : setDialogOpen(true);
    },
  };

  const mergedProps = { ...props, ..._.omit(inputProps, ['binHref', 'insertionPoint']) };
  return <>
    <DialogStylesProvider>
      <OrderCalculatorDialog
        open={dialogOpen}
        binHref={binHref}
        onClose={() => setDialogOpen(false)}
      />
    </DialogStylesProvider>
    <InLineStylesProvider insertionPoint={inputProps.insertionPoint}>
      <Button className={styles.button} {...mergedProps} />
    </InLineStylesProvider>
  </>;
}
